import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import type { PROGRAM_PAYOUT_TYPE_MAP } from '~/helpers/challenge'
import type { CurrencyData } from '~/types/api-client'
import type { ProductVariant } from '~/types/challenge'
import type { FuturesCategory } from '~/types/offer'
import type { Product } from '~/types/product'

export const ROUTES = {
  main: '/quick-checkout',
  challengeCfd: ({
    countryCode,
    program,
    ref,
  }: {
    countryCode: string
    program?: keyof typeof PROGRAM_PAYOUT_TYPE_MAP
    ref?: string
  }) => {
    let url = `/quick-checkout/challenge/cfd?country=${countryCode}`
    if (program) {
      url += `&program=${program}`
    }

    if (ref) {
      url += `&ref=${ref}`
    }
    return url
  },
  challengeFutures: ({
    countryCode,
    request,
    program,
    ref,
  }: {
    countryCode: string
    request?: Request
    program?: keyof typeof PROGRAM_PAYOUT_TYPE_MAP
    ref?: string
  }) => {
    const params = new URLSearchParams(request ? new URL(request.url).searchParams : '')
    params.set('country', countryCode)
    if (program) {
      params.set('program', String(program))
    }

    if (ref) {
      params.set('ref', ref)
    }
    return `/quick-checkout/challenge/futures?${params.toString()}`
  },
  challengeStocks(args: { countryCode: string }) {
    const { countryCode } = args

    const params = new URLSearchParams()
    params.set('country', countryCode)

    return `/quick-checkout/challenge/stocks?${params.toString()}`
  },
  special: ({ offerId, planId, countryCode }: { offerId: string; planId: number; countryCode: string }) => {
    const params = new URLSearchParams()
    params.set('landingPath', 'challenge')
    params.set('country', countryCode)
    return `/quick-checkout/challenge/special/${offerId}/${planId}?${params.toString()}`
  },
  personalDetails: ({
    ref,
    countryCode,
    from,
    program,
    offerId,
    planId,
    token,
  }: {
    countryCode: string
    from: 'cfd' | 'futures' | 'stocks' | 'guest-checkout'
    program?: keyof typeof PROGRAM_PAYOUT_TYPE_MAP
    ref?: string
    offerId?: string
    planId?: string
    token?: string
  }) => {
    let url = `/quick-checkout/personal-details?country=${countryCode}&from=${from}`

    if (program) {
      url += `&program=${program}`
    }

    if (ref) {
      url += `&ref=${ref}`
    }
    if (offerId && planId) {
      url += `&offerId=${offerId}&planId=${planId}`
    }
    if (token) {
      url += `&token=${token}`
    }
    return url
  },
  checkout: (
    countryCode: string,
    from: 'cfd' | 'futures' | 'stocks' | 'guest-checkout',
    program: keyof typeof PROGRAM_PAYOUT_TYPE_MAP,
    clientId?: string,
  ) => {
    let url = new URL('/quick-checkout/payment', window.location.origin)

    url.searchParams.set('country', countryCode)
    url.searchParams.set('from', from)
    clientId && url.searchParams.set('clientId', clientId)

    if (program) {
      url.searchParams.set('program', program)
    }

    return url.pathname + '?' + url.searchParams.toString()
  },
}

export const ROUTE_IDS = {
  challengeCfd: 'routes/quick-checkout/challenge/cfd',
  challengeFutures: 'routes/quick-checkout/challenge/futures',
  challengeStocks: 'routes/quick-checkout/challenge/stocks',
  personalDetails: 'routes/quick-checkout/personal-details',
  checkout: 'routes/quick-checkout/payment',
}

export type QCStep = {
  label: string
  slug: 'challenge' | 'personal-details' | 'payment' | 'product'
  routeId: Array<string> | string
}

export type PrefillClientDetails = {
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  whatsappNumber?: string
  whatsappSameAsPhone?: boolean
  countryKey: string
  city: string
  state: string
  postCode: string
  address: string
  acceptTerms: boolean
  subscribeNewsletter: boolean
  cfdDisclaimer: boolean
}
export type ChallengeData = {
  prefillClientDetails?: PrefillClientDetails
  [key: string]: any // TODO: add correct typing
}

export type CFDSelectionsRef = {
  program?: number | null
  type?: number | null
  balance?: number
  platform?: number | null
} | null

export const SelectionAddonTabs = {
  selection: 'selection',
  addons: 'addons',
} as const

export type QuickCheckoutOutletData = {
  challengeData: ChallengeData
  setChallengeData: (newChallengeData: ChallengeData) => void
  mainDomainUrl: string
  cfdSelectionsRef: MutableRefObject<CFDSelectionsRef>
  productVariant?: ProductVariant
  futures: {
    tab: keyof typeof SelectionAddonTabs
    setTab: Dispatch<SetStateAction<keyof typeof SelectionAddonTabs>>
    selectedChallenge: FuturesCategory | null
    setSelectedChallenge: Dispatch<SetStateAction<FuturesCategory | null>>
  }
}

export type ChallengeOutletData = {
  selectedProgram: Program
  setSelectedProgram: React.Dispatch<React.SetStateAction<number>>
} & QuickCheckoutOutletData

export type Platform = {
  id: number
  name: string // Maybe: MT4 | MT5
  displayName: string
  enabled: boolean
}

export const PROGRAMS = {
  prime: 2,
  classic: 1,
}
export type Program = number

// PASS PRODUCTS
export type ProductCheckoutStep = {
  label: string
  slug: 'product' | 'personal-details' | 'payment'
  routeId: Array<string> | string
}

export const PRODUCT_CHECKOUT_ROUTE_IDS = {
  passProduct: 'routes/checkout-step/product/pass',
  personalDetails: 'routes/checkout-step/personal-details',
  checkout: 'routes/checkout-step/payment',
}

export const PRODUCT_CHECKOUT_ROUTES = {
  main: '/checkout-step',
  passProduct: ({ countryCode }: { countryCode: string }) => {
    let url = `/checkout-step/product/pass?country=${countryCode}`
    return url
  },
  personalDetails: ({ countryCode }: { countryCode: string }) => {
    let url = `/checkout-step/personal-details?country=${countryCode}`

    return url
  },
  checkout: (countryCode: string) => {
    let url = new URL('/checkout-step/payment', window.location.origin)

    url.searchParams.set('country', countryCode)

    return url.pathname + '?' + url.searchParams.toString()
  },
}

export type PassOutletData = {
  passProductData: Product | null
  setPassProductData: Dispatch<SetStateAction<Product | null>>
  fiatCurrency: CurrencyData | null
  setFiatCurrency: Dispatch<SetStateAction<CurrencyData | null>>
  prefillClientDetails: PrefillClientDetails | null
  setPrefillClientDetails: Dispatch<SetStateAction<PrefillClientDetails | null>>
}
